import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '@docma-mm-core/config/config.service';
import { BasedataService } from '@docma-mm-core/shared/basedata.service';
import { Structure } from '@docma-mm-core/structure/structure';
import { Observable, catchError, firstValueFrom, lastValueFrom, map, of, tap, timeout } from 'rxjs';
import { Plan, PlanData, PlanMetadata } from './plan';
import { Project } from '@docma-mm-core/project/project';
import { BpApiService } from '../../bp-common/bp-api.service';
import { convertBpToMmPlan } from '../../mm-faults/mm-plans-view/services/mm-plan-utils';
import { PlanDto } from '../../bp-common/models/plan';
import { IOfflineService } from '../../mm-offline/interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../../mm-offline/interfaces/offline-service.token';
import { PlatformModeStateService } from '../../mm-offline/services/platform-mode-state.service';
import { Logger } from '@mydocma/common/src/lib/logger';



@Injectable()
export class PlanService extends BasedataService {

  private planUrl = '/plans';
  private structureUrl = '/structures/';
  private metaDataUrl = '/attachments/';

  constructor(private http: HttpClient,
    private bpApiService: BpApiService,
    configService: ConfigService,
    @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
    private platformModeStateService: PlatformModeStateService,
    private readonly logger: Logger) {
    super(configService);
  }

  private getPlansUrl(structureId: number) {
    return super.getFullUrl(this.structureUrl + structureId + this.planUrl);
  }

  private getPlanMetaDataUrl(fileId: number) {
    return super.getFullUrl(this.metaDataUrl + fileId + '/tiles');
  }

  private getAttachmentUrl(fileId: number) {
    return super.getFullUrl(this.metaDataUrl + fileId);
  }

  getPlans(project: Project, structures: Structure[]): Observable<PlanData[]> {

    if (project.BPConnected) {
      const structuresByUid: { [Uid: string]: Structure } = Object.fromEntries(structures.map(m => [m.UniqueId, m]));

      return this.bpApiService.getPlans(project.MyDocmaUid)
        .pipe(
          map(bpPlans => {
            return (bpPlans || []).map(bpPlan => {
              return convertBpToMmPlan(bpPlan, structuresByUid, project);
            });
          })
        );
    }

    const structureId = project.Structure.Id;
    const endpoint = this.getPlansUrl(structureId);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('isActive', 'true');

    return this.http
      .get<PlanData[]>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  getPlanById(planId: string): Observable<PlanDto> {
    const plan = this.bpApiService.getPlanById(planId)
    .pipe(
      map(responsePlan => {
        // TODO: Check if this is required!
        // responsePlan.currentVersion = this.getCurrentVersion(responsePlan);
        // responsePlan.currentVersion.planId = planId;
        // responsePlan.currentVersion.planTitle = responsePlan.title;
        // this.saveStructureAndStructurePath(responsePlan);
        return responsePlan;
      })
    );
    return plan;
  }

  getPlanMetaData(plan: PlanData, isDownloaded: boolean = false): Observable<PlanMetadata> {
    if (plan.BpPlan) {
      let tilesUrl = plan.BpPlan.activeVersion.tilesUrl;
      let isLocaMode = false;
      let maxNativeZoom = plan.BpPlan.activeVersion.maxLevel;
      if((this.platformModeStateService.isLocalMode || isDownloaded) && this.platformModeStateService.isLocalModeEnabled){
        const customerId = this.offlineService.currentProject?.customerId;
        const projectId = this.offlineService.currentProject?.id;
        const urlFolderName = this.platformModeStateService.clientUrl;
        const userId = this.offlineService.userId;
        tilesUrl = `/assets/capacitor/${urlFolderName}/users/${userId}/customers/${customerId}/projects/${projectId}/plans/${plan.Id}`;
        isLocaMode = true;
        if(!isDownloaded){
          maxNativeZoom = 3; // when not downloaded, we only have the base tiles which have a max zoom level of 3
        }
      }
      return of({
        ImageHeight: plan.BpPlan.activeVersion.renderHeight,
        ImageWidth: plan.BpPlan.activeVersion.renderWidth,
        MaxZoomLevel: plan.BpPlan.activeVersion.maxLevel,
        TilesUrl: tilesUrl + '/{z}/{x}_{y}.jpg',
        IsLocalMode: isLocaMode,
        MaxNativeZoom: maxNativeZoom

      } as PlanMetadata)
    }

    const fileId = +plan.Id;
    const endpoint = this.getPlanMetaDataUrl(fileId);


    return this.http
      .get<PlanMetadata>(endpoint, {headers:  this.getHeaders()})
      .pipe(
        map(md=> {
          md.MaxNativeZoom = md.MaxZoomLevel;
          if((this.platformModeStateService.isLocalMode || isDownloaded) && this.platformModeStateService.isLocalModeEnabled){
            const customerId = this.offlineService.currentProject?.customerId;
            const projectId = this.offlineService.currentProject?.id;
            const urlFolderName = this.platformModeStateService.clientUrl;
            const userId = this.offlineService.userId;
            md.TilesUrl = `/assets/capacitor/${urlFolderName}/users/${userId}/customers/${customerId}/projects/${projectId}/plans/${fileId}/{z}/{x}_{y}.jpg`;
            md.IsLocalMode = true;
            if(!isDownloaded){
              md.MaxNativeZoom = 3; // when not downloaded, we only have the base tiles which have a max zoom level of 3
            }
          }
          else{
            md.TilesUrl =  this.getFullUrl(`/attachments/${fileId}/tiles/{z}/{x}/{y}`);
          }
          
          return md;
        }),
        catchError(e =>  this.handleError(e))
      );
  }

  getPlanZip(url:string): Observable<any> {
    const headers = new HttpHeaders({
      'Accept': 'application/zip'
    });

    return this.http.get(url, {
      headers: headers,
      responseType: 'blob'
    }).pipe(
      catchError(e => this.handleError(e))
    );
  }


  getStructureChildIds(node: Structure, childIds: number[]) {
    node.Children.forEach((child) => {
      childIds.push(child.Id);
      this.getStructureChildIds(child, childIds);
    });
  }


  planContainsText(plan: Plan, filterTextLowerCase: string) {
    return plan.data.Description?.toLowerCase().includes(filterTextLowerCase)    || plan.data.Structure?.Name1.toLowerCase().includes(filterTextLowerCase) ||
           plan.data.Structure?.Name2.toLowerCase().includes(filterTextLowerCase) || plan.data.StructurePath?.toLowerCase().includes(filterTextLowerCase)   ||
           plan.data.Remark?.toLowerCase().includes(filterTextLowerCase);
  }

  async downloadPlan(plan: Plan): Promise<boolean>{
    try{
      
      let planId = plan.id.toString();
      let blob;
      if(plan.loadState.data.BpPlan){
        const url = plan.loadState.data.BpPlan.activeVersion.dzUrl;
        planId = plan.loadState.data.BpPlan.id
        blob = await firstValueFrom(this.bpApiService.getPlanByUrl(url));
      }
      else{
        const url = this.getAttachmentUrl(plan.id);
        blob = await firstValueFrom(this.getPlanZip(url));
      }
      
      return await this.offlineService.unzipAndSave(blob, planId, false);
    }catch(error){
      this.logger.error(error, '', 'PlanService');
      return false;
    }
  }

  async removePlan(plan: Plan): Promise<boolean>{
    try {
      let planId = plan.id.toString();
      if(plan.loadState.data.BpPlan){
        planId = plan.loadState.data.BpPlan.id
      }
      return await this.offlineService.removePlan(planId);
    } catch (error) {
      return false;
    }
  }

  async downloadBaseTiles(plan: PlanData): Promise<boolean>{
    try{
      let planId = plan.Id;
      let blob;
      if(plan.BpPlan){
        const urlsToTry = [plan.BpPlan.activeVersion.min_dzUrl, plan.BpPlan.activeVersion.dzUrl].filter(Boolean);
        planId = plan.BpPlan.id

        for(const url of urlsToTry){
          try {
            blob = await firstValueFrom(this.bpApiService.getPlanByUrl(url));
            break;
          } catch (error) {
            if(error.status === 404) {
              console.info('Base tiles not found:',planId, url);
              continue;
            }
            else{
              throw error;
            }
          }
        }
      }
      else{
        const url = this.getAttachmentUrl(+plan.Id);
        blob = await firstValueFrom(this.getPlanZip(url));
      }
      
      if(blob){
        return await this.offlineService.unzipAndSave(blob, planId, true);
      }
      return false;
      
    }catch(error){
      this.logger.error(error, '', 'PlanService');
      return false;
    }
  }

  async isPlanDownloaded(planId: string): Promise<boolean> {
    try {
      return await this.offlineService.isPlanDownloaded(planId);
    } catch(error) {
      this.logger.error(error, '', 'PlanService');
      return false;
    }
  }

  async getPlanTilesMetaData(plan:PlanData): Promise<any> {
    const isDownloaded = await this.isPlanDownloaded(plan.Id?.toString());
    const planMetaData = await firstValueFrom(this.getPlanMetaData(plan, isDownloaded));
    await this.offlineService.saveDataByFolderPath(planMetaData,"attachments",plan.Id,"tiles")
  }
}
